import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useStateQuery } from '../../api/state';

const THRESHOLD_MS = 30_000;

export function ConnectionBanner() {
  const q = useStateQuery();
  // 0 means "not currently failing". Any new error timestamp re-arms the
  // threshold, which is what the old `Date.now() - errorUpdatedAt` comparison
  // did too — it just happened to be evaluated on whichever render came next,
  // so the banner appeared late. Subscribing to a timer that fires exactly at
  // the threshold makes it appear on time; the snapshot is a boolean that only
  // ever flips false→true for a given errorUpdatedAt, so it cannot loop.
  const failingSince = q.isError && q.errorUpdatedAt ? q.errorUpdatedAt : 0;

  const subscribe = useMemo(
    () => (onChange: () => void) => {
      if (!failingSince) return () => {};
      const id = setTimeout(onChange, Math.max(0, failingSince + THRESHOLD_MS - Date.now()));
      return () => { clearTimeout(id); };
    },
    [failingSince],
  );
  const getThresholdPassed = useCallback(
    () => failingSince !== 0 && Date.now() - failingSince >= THRESHOLD_MS,
    [failingSince],
  );
  const thresholdPassed = useSyncExternalStore(subscribe, getThresholdPassed);

  const lastOk = q.dataUpdatedAt;
  if (!q.isError) return null;
  if (!thresholdPassed) return null;
  const since = lastOk ? new Date(lastOk).toLocaleTimeString() : 'never';
  return (
    <div className="banner danger" role="alert">
      Last successful update: {since}. Retrying…{' '}
      <button className="secondary" onClick={() => q.refetch()}>Retry</button>
    </div>
  );
}
